import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet } from "../../services/api";
import type { Product, Location, InventoryRecord } from "../../types";

export default function StockLevelsTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [products, setProducts] = useState<Product[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [inventory, setInventory] = useState<InventoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterLocation, setFilterLocation] = useState("");

  useEffect(() => { if (orgId) loadData(); }, [orgId]);

  async function loadData() {
    try {
      const [prods, locs, inv] = await Promise.all([
        apiGet<Product[]>(`/organizations/${orgId}/products`),
        apiGet<Location[]>(`/organizations/${orgId}/locations`),
        apiGet<InventoryRecord[]>(`/organizations/${orgId}/inventory`),
      ]);
      setProducts(prods);
      setLocations(locs);
      setInventory(inv);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const getStock = (productId: string, locationId?: string) => {
    if (locationId) {
      const rec = inventory.find((i: any) => i.productId === productId && i.locationId === locationId);
      return (rec as any)?.quantity ?? "—";
    }
    // Total across all locations
    return inventory
      .filter((i: any) => i.productId === productId)
      .reduce((sum: number, i: any) => sum + (i.quantity || 0), 0);
  };

  const getLocName = (id: string) => (locations.find((l: any) => l.locationId === id) as any)?.name || id;

  if (loading) return <div className="empty-state">Loading stock levels...</div>;

  const filteredProducts = products.filter((p: any) => p.status === "active");

  return (
    <div>
      <div className="org-mgmt-header">
        <h2>Stock Levels</h2>
        <select value={filterLocation} onChange={(e) => setFilterLocation(e.target.value)} style={{ padding: "0.5rem 0.75rem", border: "1px solid #d1d5db", borderRadius: "6px", fontSize: "0.85rem" }}>
          <option value="">All Locations (Total)</option>
          {locations.map((l: any) => <option key={l.locationId} value={l.locationId}>{l.name}</option>)}
        </select>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="empty-state"><p>No products in catalog. Add products first.</p></div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>{filterLocation ? `Stock at ${getLocName(filterLocation)}` : "Total Stock"}</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map((prod: any) => {
              const qty = getStock(prod.productId, filterLocation || undefined);
              const isLow = typeof qty === "number" && qty <= 5 && qty > 0;
              const isOut = qty === 0;
              return (
                <tr key={prod.productId}>
                  <td style={{ fontWeight: 500 }}>{prod.name}</td>
                  <td><code style={{ fontSize: "0.85rem", background: "#f3f4f6", padding: "0.15rem 0.4rem", borderRadius: "4px" }}>{prod.sku || "—"}</code></td>
                  <td style={{ fontWeight: 600 }}>{qty}</td>
                  <td>
                    {isOut ? <span className="status-badge inactive">Out of Stock</span> :
                     isLow ? <span className="status-badge invited">Low Stock</span> :
                     typeof qty === "number" ? <span className="status-badge active">In Stock</span> :
                     <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>Not tracked</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
